import { memo, useRef } from "react";
import Grid from "@mui/material/Grid";
import {
  maxLength,
  minLength,
  minValue,
  number,
  required,
  TextInput,
  useDataProvider,
} from "react-admin";
import MuiDatePickerInput from "../../../common/MuiDatePickerInput";

import { toCurrency } from "../../../../utils/formatter";
import InputAdornment from "@mui/material/InputAdornment";
import { useFormContext } from "react-hook-form";
import parserFormatNumInput from "../../../../utils/parserFormatNumInput";
import PurchaseOrderReference from "../../common/PurchaseOrderReference";
import CalculationResult from "../../common/CalculationResult";
import useEnterToAddRowAndFocus from "../../../../utils/useEnterToAddRowAndFocus";

const equalOrLessThanTotal = (value, allValues, elm) => {
  let remain = allValues.total;
  let count = 0;
  const splite = elm.source.split(".");
  let skipProcessLast = 0;
  for (let i = 0; i < allValues.factory_debt_correction_items.length; i++) {
    const elm = allValues.factory_debt_correction_items[i];
    if (+splite[1] === i) {
      skipProcessLast = elm.amount;
      continue;
    }
    count += +elm.amount;
    if (+count > +allValues.total) {
      return (
        "Must be equal or less than " +
        toCurrency(false).format(remain) +
        " (remaining of total correction)"
      );
    }
    remain -= +elm.amount;
  }
  count += +skipProcessLast;
  if (+count > +allValues.total) {
    return (
      "Must be equal or less than " +
      toCurrency(false).format(remain) +
      " (remaining of total correction)"
    );
  }
  return undefined;
};

const uniqueItem = (value, allValues, elm) => {
  let count = 0;
  for (let i = 0; i < allValues.factory_debt_correction_items.length; i++) {
    const elm = allValues.factory_debt_correction_items[i];
    if (value === elm.purchase_order_id) {
      if (count > 0) {
        return "Duplicate is not allowed";
      }
      count += 1;
    }
  }
  return undefined;
};

const validateCode = [required(), minLength(36), maxLength(36), uniqueItem];
const validateAmount = [
  required(),
  number(),
  minValue(1),
  equalOrLessThanTotal,
];

const CstInput = ({
  formData,
  purchaseOrderIdSource,
  dueDateSource,
  debtSource,
  transactionDateSource,
  amountSource,
  addRow, // fungsi untuk menambah baris baru dari ArrayInput
}) => {
  const { setValue } = useFormContext();
  const dataProvider = useDataProvider();

  // 🔹 Ref untuk amount input (correction)
  const amountRef = useRef(null);

  // 🔹 Pasang Enter → tambah baris dan fokus ke purchase_order_id baris baru
  useEnterToAddRowAndFocus(amountRef, addRow, 'input[name*="purchase_order_id"]');

  const onCodeChangeHandler = async (id) => {
    if (id.length !== 36) return;
    try {
      const { created_at, company_debt } =
        await dataProvider.getListNoParams(
          "purchase-orders/" + encodeURIComponent(id)
        );
      setValue(transactionDateSource, created_at);
      setValue(dueDateSource, company_debt.due_date);
      setValue(debtSource, company_debt.remain);
    } catch (error) {}
  };

  return (
    <Grid container spacing={2} sx={{ "& .MuiGrid-item": { py: 0 }, mt: 0 }}>
      <Grid item xs={12} lg={5.4}>
        <PurchaseOrderReference
          source={purchaseOrderIdSource}
          validate={validateCode}
          dependOn="supplier_id"
          queryName="supplier_id"
          resource="purchase_orders"
          reference="purchase-orders"
          defaultFilters={{
            "CompanyDebt.settled_date": "IS NULL",
            "CompanyDebt.remain": ">0",
          }}
          formData={formData}
          onChange={onCodeChangeHandler}
        />
      </Grid>

      <Grid item xs={12} lg={1.2}>
        <MuiDatePickerInput
          variant="outlined"
          source={transactionDateSource}
          label="mine.label.transaction_date"
          disabled
          fullWidth
          textFieldProps={{
            sx: { minWidth: 130 },
          }}
        />
      </Grid>

      <Grid item xs={12} lg={1.2}>
        <MuiDatePickerInput
          variant="outlined"
          source={dueDateSource}
          label="mine.label.due_date"
          disabled
          fullWidth
          textFieldProps={{
            sx: { minWidth: 130 },
          }}
        />
      </Grid>

      <Grid item xs={12} lg={1.4}>
        <TextInput
          variant="outlined"
          source={debtSource}
          label="mine.label.debt"
          fullWidth
          disabled
          InputProps={{
            startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
          }}
          {...parserFormatNumInput}
        />
      </Grid>

      {/* 🔹 Correction (amount) dengan Enter */}
      <Grid item xs={12} lg={1.4} ref={amountRef}>
        <TextInput
          variant="outlined"
          source={amountSource}
          label="mine.label.correction"
          fullWidth
          validate={validateAmount}
          InputProps={{
            startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
          }}
          {...parserFormatNumInput}
        />
      </Grid>

      <Grid item xs={12} lg={1.4}>
        <CalculationResult
          variant="outlined"
          label="mine.label.remain"
          fullWidth
          dependOn={[debtSource, amountSource, "factory_debt_correction_journals.0.debit"]}
          InputProps={{
            startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
          }}
          {...parserFormatNumInput}
        />
      </Grid>
    </Grid>
  );
};

const propsAreEqual = (prevProps, nextProps) => {
  return prevProps.quantity === nextProps.quantity && prevProps.code === nextProps.code;
};

const Beruk = memo(CstInput, propsAreEqual);

const Inputs = ({ formData, scopedFormData, getSource, addRow }) => {
  return (
    <Beruk
      {...scopedFormData}
      formData={formData}
      purchaseOrderIdSource={getSource("purchase_order_id")}
      transactionDateSource={getSource("transaction_date")}
      dueDateSource={getSource("due_date")}
      debtSource={getSource("debt")}
      amountSource={getSource("amount")}
      addRow={addRow} // harus diteruskan dari ArrayInput
    />
  );
};

export default Inputs;
